import { useState, useEffect, useCallback } from "react";
import api from "../api/client";
import Navbar from "../components/layout/Navbar";
import MobileNavbar from "../components/layout/MobileNavbar";
import useAuth from "../hooks/useAuth";
import useIsMobile from "../hooks/useIsMobile";

function ConfirmDialog({ title, message, onConfirm, onCancel, confirmLabel = "Confirm", danger = true }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
        <h3 className="text-base font-semibold text-gray-800 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-4">{message}</p>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">Cancel</button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm rounded text-white ${danger ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function TLFormModal({ tl, onClose, onSaved }) {
  const isEdit = !!tl;
  const [name, setName] = useState(tl?.name || "");
  const [isActive, setIsActive] = useState(tl?.is_active ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError("Name is required."); return; }
    setSaving(true);
    setError("");
    try {
      if (isEdit) {
        await api.put(`/team-leaders/${tl.id}`, { name: name.trim(), is_active: isActive });
      } else {
        await api.post("/team-leaders", { name: name.trim() });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          {isEdit ? "Edit Team Leader" : "Add Team Leader"}
        </h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter Team Leader name"
              autoFocus
            />
          </div>
          {isEdit && (
            <div className="mb-4">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Active
              </label>
              <p className="text-xs text-gray-500 mt-1">Inactive team leaders won't appear in dropdowns.</p>
            </div>
          )}
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded hover:bg-gray-100">Cancel</button>
            <button
              type="submit"
              disabled={saving || !name.trim()}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : isEdit ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function TeamLeadersPage() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("team_leaders.manage");
  const isMobile = useIsMobile();
  const [tls, setTls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editTl, setEditTl] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [error, setError] = useState("");

  const fetchTls = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team-leaders");
      setTls(data);
    } catch {
      setTls([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTls(); }, [fetchTls]);

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setError("");
    try {
      await api.delete(`/team-leaders/${pendingDelete.id}`);
      setPendingDelete(null);
      fetchTls();
    } catch (err) {
      setError(err.response?.data?.detail || "Delete failed.");
      setPendingDelete(null);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-100 overflow-hidden">
      {isMobile ? <MobileNavbar /> : <Navbar />}

      <main className="flex-1 flex flex-col min-h-0 px-4 lg:px-6 pt-4 pb-2 w-full">
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <h1 className="text-xl font-semibold text-gray-800">Team Leaders</h1>
          {canManage && (
            <button
              onClick={() => { setEditTl(null); setShowForm(true); }}
              className="bg-blue-600 text-white text-sm px-4 py-2 rounded hover:bg-blue-700"
            >
              Add Team Leader
            </button>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-4 py-3 mb-3 flex-shrink-0">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-500">Loading...</div>
        ) : (
          <div className="flex-1 min-h-0 border rounded-lg overflow-hidden">
            <div className="h-full overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 bg-gray-50">Name</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 bg-gray-50">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 bg-gray-50">Assigned JOs</th>
                    {canManage && (
                      <th className="px-4 py-3 text-left font-medium text-gray-600 bg-gray-50">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {tls.length === 0 ? (
                    <tr>
                      <td colSpan={canManage ? 4 : 3} className="text-center py-10 text-gray-400">
                        No team leaders yet.
                      </td>
                    </tr>
                  ) : (
                    tls.map((t) => (
                      <tr key={t.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-800 font-medium">{t.name}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${t.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"}`}>
                            {t.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{t.assigned_count}</td>
                        {canManage && (
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => { setEditTl(t); setShowForm(true); }}
                                className="text-xs px-3 py-1 border border-blue-300 text-blue-700 rounded hover:bg-blue-50"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => setPendingDelete(t)}
                                className="text-xs px-3 py-1 border border-red-300 text-red-700 rounded hover:bg-red-50"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {showForm && (
        <TLFormModal
          tl={editTl}
          onClose={() => setShowForm(false)}
          onSaved={fetchTls}
        />
      )}

      {pendingDelete && (
        <ConfirmDialog
          title="Delete Team Leader"
          message={`Delete "${pendingDelete.name}"? This cannot be undone. If this TL has assigned job orders, you'll need to deactivate instead.`}
          confirmLabel="Delete"
          onConfirm={handleDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}
